<template>
  <div class="org-chart-wrapper">
    <div class="controls">
      <v-autocomplete
        v-model="searchSelected"
        :items="autocompleteItems"
        :search-input.sync="searchQuery"
        hide-no-data
        hide-details
        placeholder="Search"
        item-value="id"
        item-text="searchLabel"
        return-object
        dense
        outlined
        clearable
        class="search-field"
        prepend-inner-icon="mdi-magnify"
        :filter="() => true"
        @change="goToUser"
      >
        <template v-slot:item="{ item }">
          <v-list-item-content>
            <v-list-item-title>{{ item.blueTag }}</v-list-item-title>
          </v-list-item-content>
        </template>
        <template v-slot:selection="{ item }">
          <span v-if="item">{{ item.blueTag }}</span>
        </template>
      </v-autocomplete>
      <button 
        v-if="!dragEnabled" 
        @click="enableDrag" 
        class="action-btn"
        :disabled="loading || !canEdit"
      >
        Organize
      </button>
      <button  
        v-if="!dragEnabled" 
        @click="swapLayout" 
        class="action-btn"
        :disabled="loading "
      >
        Swap layout
      </button>
      
      <div v-else class="drag-actions">
        <button class="action-btn primary" @click="disableDrag">Save</button>
        <button 
          class="action-btn" 
          :disabled="undoActions.length === 0" 
          @click="undo"
        >
          Undo
        </button>
        <button 
          class="action-btn" 
          :disabled="redoActions.length === 0" 
          @click="redo"
        >
          Redo
        </button>
        <button class="action-btn warning" @click="cancelDrag">Cancel</button>
      </div>
      <v-switch label="My Tree" class="ml-5 pa-0 mt-3" v-model="myTree"/>
    </div>

    <div class="chart-and-sidebar">
      <div v-if="loading" class="loading-container">
        <div class="loading-message">Cargando usuarios...</div>
      </div>
      <div ref="chartContainer" class="chart-container" @contextmenu.prevent="onChartContextMenu"></div>

      <div v-if="sidebarOpen" class="node-sidebar">
          <button class="sidebar-close" @click="closeSidebar">
            <v-icon small>mdi-close</v-icon>
          </button>

          <v-menu
            v-model="selectingUser"
            :close-on-content-click="false"
            offset-y
            left
            nudge-bottom="8"
            min-width="240"
          >
            <template #activator="{ on }">
              <div class="sidebar-avatar" v-on="on" title="Cambiar usuario">
                <img
                  :src="selectedNode.profilePicSrc || ''"
                  :alt="selectedNode.fullName"
                  class="sidebar-avatar-img"
                />
                <div class="sidebar-avatar-edit-overlay">
                  <v-icon small color="white">mdi-pencil</v-icon>
                </div>
              </div>
            </template>

            <v-card style="min-width:240px; max-height:320px; display:flex; flex-direction:column; background-color: var(--v-newDesignBackground-base)">
              <div class="px-2 pt-2">
                <v-text-field
                  ref="userSearchField"
                  v-model="userSearch"
                  dense
                  outlined
                  hide-details
                  placeholder="Buscar..."
                  prepend-inner-icon="mdi-magnify"
                  @keydown.esc="selectingUser = false"
                />
              </div>
              <v-list dense class="overflow-y-auto">
                <v-list-item
                  v-for="user in originalUsers"
                  :key="user.id"
                  @click="onSelectUser(user.id)"
                  style="background-color: var(--v-newDesignBackground-base)"
                >
                  <v-list-item-avatar size="32">
                    <v-img :src="user.profilePicSrc" />
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>{{ user.blueTag }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card>
          </v-menu>

          <div class="sidebar-name">{{ selectedNode.fullName }}</div>
          <div class="sidebar-bluetag">@{{ selectedNode.blueTag }}</div>

          <v-divider class="my-3" />
          

          <div class="sidebar-section">
            <div v-if="selectedNode.jobTitle || selectedNode.roles" class="sidebar-row">
              <v-icon small class="sidebar-icon">mdi-briefcase-outline</v-icon>
              <span>{{ selectedNode.jobTitle || selectedNode.roles }}</span>
            </div>
            <div v-if="selectedNodeManager" class="sidebar-row">
              <v-icon small class="sidebar-icon">mdi-account-supervisor-outline</v-icon>
              <span>{{ selectedNodeManager }}</span>
            </div>
          </div>

          <v-divider class="my-3" />
          <v-row dense justify="center">
            <v-col class="col-login" :class="'mb-1'">
              <span>Manager</span>
              <q-user-autocomplete
                :handleEsc="true"
                dense
                :handleSave="true"
                @save="onManagerChange"
                class="user-autocomplete"
                outlined
                v-model="selectedNode.managerId" :items="filteredSidebarUsers" 
                item-value="id" item-text="blueTag" item-subtitle="fullName"
              >
              </q-user-autocomplete>
            </v-col>
          </v-row>
        </div>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import { OrgChart } from 'd3-org-chart';
import logo from '../../../public/logo-surf.png';

export default {
  name: 'OrgChart',
  data() {
    return {
      dragEnabled: false,
      undoActions: [],
      redoActions: [],
      users: [],
      originalUsers: [],
      loading: false,
      canEdit: false,
      searchQuery: '',
      searchSelected: null,
      canEditUsers: false,
      orientation: 'top',
      myTree: false,
      currentUserId: null,
      sidebarOpen: false,
      selectedNode: {},
      selectingUser: false,
      userSearch: '',
    };
  },
  watch: {
    searchSelected(val) {
      if (!val && this.chart) {
        this.chart.clearHighlighting().render();
      }
    },
    selectingUser(val) {
      if (val) {
        this.userSearch = '';
        this.$nextTick(() => {
          this.$refs.userSearchField && this.$refs.userSearchField.focus();
        });
      }
    },
    myTree(val) {
      if (val) {
        this.applyMyTreeFilter();
      } else {
        this.users = this.normalizeUsersForOrgChart([...this.originalUsers]);
        this.chart.data(this.users).render().fit();
      }
    },
  },
  computed: {
    searchableUsers() {
      return this.users.filter(u => u.id !== 'virtual-root-org');
    },
    searchResults() {
      if (!this.searchQuery || this.searchQuery.length < 2) {
        return [];
      }
      const q = this.searchQuery.toLowerCase().trim();
      return this.searchableUsers
        .filter(u => {
          const blueTag = (u.blueTag || '').toLowerCase();
          const fullName = (u.fullName || '').toLowerCase();
          const email = (u.email || '').toLowerCase();
          return blueTag.includes(q) || fullName.includes(q) || email.includes(q);
        })
        .map(u => ({ ...u, searchLabel: `${u.blueTag || ''} ${u.fullName || ''}`.trim() }))
        .slice(0, 15);
    },
    autocompleteItems() {
      const withLabel = (list) =>
        list.map(u => ({ ...u, searchLabel: `${u.blueTag || ''} ${u.fullName || ''}`.trim() }));
      if (this.searchQuery && this.searchQuery.length >= 2) {
        return this.searchResults;
      }
      return withLabel(this.searchableUsers);
    },
    filteredSidebarUsers() {
      const q = (this.userSearch || '').toLowerCase().trim();
      const virtualRoot = this.users.find(u => u.id === 'virtual-root-org');
      const regular = this.originalUsers;
      const all = virtualRoot ? [virtualRoot, ...regular] : regular;
      if (!q) return all;
      return all.filter(u =>
        (u.blueTag || '').toLowerCase().includes(q) ||
        (u.fullName || '').toLowerCase().includes(q)
      );
    },
    selectedNodeManager() {
      if (!this.selectedNode) return null;
      const managerId = this.selectedNode.managerId;
      if (!managerId) return null;
      const manager = this.users.find(u => u.id === managerId);
      return manager ? (manager.fullName || manager.blueTag) : null;
    },
  },
  mounted() {
    document.title = 'Organization Chart'
    this.chart = null;
    this.dragNode = null;
    this.dropNode = null;
    this.dragStartX = 0;
    this.dragStartY = 0;
    this.isDragStarting = false;
    this.$blueSystem.getCurrentUser().then(user => {
      this.currentUserId = user.id;
    });
    this.loadUsers();
    this.$security.hasRequiredPermission('add').then(canEdit => {
      this.canEdit = canEdit;
    });
    this.$security.hasRequiredPermission('full_access', 14).then(canEditUsers => {
      this.canEditUsers = canEditUsers;
    })
  },
  beforeDestroy() {
    this.chart = null;
  },
  methods: {
    swapLayout() {
      this.chart.layout(this.orientation === 'top' ? 'left' : 'top').setCentered('virtual-root-org').render().fit();
      this.orientation = this.orientation === 'top' ? 'left' : 'top';
    },
    loadUsers() {
      this.loading = true;
      this.$http
        .get('/api/user?forOrganizationChart=true&usersOnly=true&active=true&onlyInterns=true')
        .then(response => {
          let allUsers = response.data.map(user => ({
            ...user,
            managerId: user.managerId || user.parentId || null
          }));

          allUsers.forEach(this.$q.profilePicSrc);

          this.originalUsers = [...allUsers];

          if (this.myTree) {
            const currentUser = allUsers.find(u => u.id === this.currentUserId);
            const subtree = currentUser ? this.getSubtree(this.currentUserId, allUsers) : [];
            this.users = currentUser && subtree.length > 0
              ? [{ ...currentUser, managerId: null }, ...subtree]
              : this.normalizeUsersForOrgChart([...allUsers]);
          } else {
            this.users = this.normalizeUsersForOrgChart([...allUsers]);
          }

          this.initChart();
          this.loading = false;
        })
        .catch(response => {
          window.getApp.error("No se pudieron cargar los usuarios.");
          console.error(response);
          this.loading = false;
        });
    },
    
    normalizeUsersForOrgChart(users) {
      // Siempre usar virtual root "Blue People": todos los usuarios sin ManagerId son hijos de la organización
      return this.createVirtualRoot(users);
    },

    createVirtualRoot(users) {
      const virtualRoot = {
        id: 'virtual-root-org',
        fullName: 'Blue People',
        roles: 'Organización',
        managerId: null,
        active: true,
        type: 0,
        email: '',
        blueTag: 'Blue People',
        jobTitle: '',
        badges: [],
        customerProjects: [],
        department: 0,
        experienceLevelName: '',
        profilePicSrc: logo
      };

      const normalizedUsers = users.map(user => {
        if (!user.managerId) {
          return { ...user, managerId: virtualRoot.id };
        }
        return user;
      });

      return [virtualRoot, ...normalizedUsers];
    },
    initChart() {
      if (!this.$refs.chartContainer || !this.users || this.users.length === 0) {
        return;
      }

      const vm = this;

      this.chart = new OrgChart()
          .defs((d) => {
            return `
              <marker id="arrow-${d.from + "_" + d.to}" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                <path d="M 0 0 L 10 3.5 L 0 7" fill="#E27396" />
              </marker>
            `;
          })
          .nodeHeight((d) => 100)
          .nodeWidth((d) => 180)
          .childrenMargin((d) => 50)
          .compactMarginBetween((d) => 35)
          .compactMarginPair((d) => 30)
          .neighbourMargin((a, b) => 20)
          .container(this.$refs.chartContainer)
          .parentNodeId((d) => {
            if (d.data) {
              return d.data.managerId || null;
            }
            return d.managerId || null;
          })
          .data(this.users)
          .initialExpandLevel(10)
          .nodeContent((d) => this.generateContent(d))
          .nodeEnter(function (node) {
            d3.select(this).call(
              d3.drag()
                .filter(function () {
                  return vm.dragEnabled && this.classList.contains('draggable');
                })
                .on('start', function (event, d) {
                  vm.onDragStart(this, event, d);
                })
                .on('drag', function (event, d) {
                  vm.onDrag(this, event);
                })
                .on('end', function (event, d) {
                  vm.onDragEnd(this, event);
                })
            );
          })
          .nodeUpdate(function (d) {
            const el = d3.select(this);
            const nodeId = d.data?.id ?? d.id;
            if (nodeId === 'virtual-root-org') {
              el.classed('droppable', true); 
              el.classed('draggable', false);
              el.style('cursor', 'default');
              el.on('click', null);
            } else {
              el.classed('droppable', true);
              el.classed('draggable', true);
              if (!vm.dragEnabled) {
                el.style('cursor', 'pointer');
                el.on('click', function () {
                  vm.onNodeClick({ id: nodeId });
                });
              } else {
                el.style('cursor', '');
                el.on('click', null);
              }
            }
          })
          .linkUpdate(function (d) {
            const el = d3.select(this);
            const highlighted = d.data._upToTheRootHighlighted;
            el.classed('link-highlighted', highlighted)
              .attr('stroke', highlighted ? '#E27396' : '#167bd7')
              .attr('stroke-width', highlighted ? 2 : 2)
              .attr('stroke-opacity', highlighted ? 1 : 0.6);
            if (highlighted) el.raise();
          })
          .render()
          .fit();
    },


    onDragStart(element, event, node) {
      this.dragNode = node;
      const width = node.width || 220;
      const half = width / 2;
      
      this.dragStartX = event.x - half;
      this.dragStartY = event.y;
      this.isDragStarting = true;

      d3.select(element).classed('dragging', true);
    },

    onDrag(element, event) {
      if (!this.dragNode) return;

      const state = this.chart.getChartState();
      const g = d3.select(element);

      if (this.isDragStarting) {
        this.isDragStarting = false;
        this.$refs.chartContainer.classList.add('dragging-active');

        g.raise();

        const descendants = this.dragNode.descendants();
        const linksToRemove = [...(descendants || []), this.dragNode];
        const nodesToRemove = descendants.filter(
          (x) => x.data.id !== this.dragNode.data.id
        );

        state.linksWrapper
          .selectAll('path.link')
          .data(linksToRemove, (d) => state.nodeId(d))
          .remove();

        if (nodesToRemove) {
          state.nodesWrapper
            .selectAll('g.node')
            .data(nodesToRemove, (d) => state.nodeId(d))
            .remove();
        }
      }

      this.dropNode = null;
      
      const cP = {
        midX: event.x + this.dragNode.width / 2,
        midY: event.y + this.dragNode.height / 2,
      };

      const allNodes = d3.selectAll('g.node:not(.dragging)');
      allNodes.select('rect').attr('fill', 'none');
      allNodes.classed('drop-target', false);

      const vm = this;

      allNodes
        .filter(function (d2) {
          const cPInner = {
            left: d2.x,
            right: d2.x + d2.width,
            top: d2.y,
            bottom: d2.y + d2.height,
          };

          if (
            cP.midX > cPInner.left &&
            cP.midX < cPInner.right &&
            cP.midY > cPInner.top &&
            cP.midY < cPInner.bottom &&
            this.classList.contains('droppable')
          ) {
            vm.dropNode = d2;
            return d2;
          }
        })
        .each(function () {
          d3.select(this).classed('drop-target', true);
        })
        .select('rect')
        .attr('fill', '#E0EDFE');

      this.dragStartX += event.dx;
      this.dragStartY += event.dy;
      g.attr('transform', `translate(${this.dragStartX},${this.dragStartY})`);
    },

    onDragEnd(element, event) {
      this.$refs.chartContainer.classList.remove('dragging-active');
      d3.selectAll('g.node').classed('drop-target', false);

      if (!this.dragNode) return;

      d3.select(element).classed('dragging', false);

      if (!this.dropNode || (this.dragNode.parent && this.dragNode.parent.id === this.dropNode.id)) {
        this.chart.render();
        this.dragNode = null;
        this.dropNode = null;
        return;
      }

      d3.select(element).remove();

      const data = this.chart.getChartState().data;
      const node = data.find((x) => x.id === this.dragNode.data.id);
      const oldManagerIdFromNode = node.managerId || node.data?.managerId;
      const oldManagerId = oldManagerIdFromNode === 'virtual-root-org' ? null : oldManagerIdFromNode;
      
      const dropNodeData = data.find((x) => x.id === this.dropNode.id);
      const isDroppingOnRoot = this.dropNode.id === 'virtual-root-org' || 
                               (!dropNodeData?.managerId && !dropNodeData?.data?.managerId);
      
      const newManagerIdForChart = this.dropNode.id;
      
      node.managerId = newManagerIdForChart;
      if (node.data) {
        node.data.managerId = newManagerIdForChart;
      }
      
      const normalizedUser = this.users.find((u) => u.id === this.dragNode.data.id);
      if (normalizedUser) {
        normalizedUser.managerId = newManagerIdForChart;
      }
      
      const originalUser = this.originalUsers.find((u) => u.id === this.dragNode.data.id);
      if (originalUser) {
        originalUser.managerId = newManagerIdForChart;
      }

      this.redoActions = [];
      this.undoActions.push({
        id: this.dragNode.data.id,
        managerId: oldManagerId,
      });

      this.dropNode = null;
      this.dragNode = null;
      
      this.chart.data(this.users).render();
      if(isDroppingOnRoot && originalUser.recessApprovers.length == 1) {
        window.getApp.error('The user has only one recess approver and cannot be dropped on the root');
        this.undo();
      }
    },

    enableDrag() {
      this.dragEnabled = true;
      this.$refs.chartContainer.classList.add('drag-enabled');
      this.chart.render();
    },

    async disableDrag() {
      if (this.undoActions.length > 0) {
        try {
          const changes = this.undoActions.map(action => {
            const user = this.users.find(u => u.id === action.id);
            let managerId = user ? (user.managerId || null) : null;
            if (managerId === 'virtual-root-org') {
              managerId = null;
            }
            return {
              id: action.id,
              managerId: managerId
            };
          });

          const allUsersAfterChanges = this.originalUsers.map(originalUser => {
            const change = changes.find(c => c.id === originalUser.id);
            if (change) {
              return { ...originalUser, managerId: change.managerId };
            }
            return originalUser;
          });
          
          await this.$http.post('api/user/updateManagerIds', {
            changes: changes
          });

          await this.loadUsers();

          window.getApp.success('Cambios guardados correctamente');
        } catch (error) {
          console.error('Error al guardar cambios:', error);
          // El interceptor de axios ya muestra el error automáticamente
          // No limpiar los arrays si hay error, para que el usuario pueda intentar de nuevo
          return;
        }
      }

      this.dragEnabled = false;
      this.$refs.chartContainer.classList.remove('drag-enabled');
      this.undoActions = [];
      this.redoActions = [];
      this.chart
    },

    cancelDrag() {
      if (this.undoActions.length > 0) {
        const data = this.chart.getChartState().data;
        [...this.undoActions].reverse().forEach((action) => {
          const node = data.find((x) => x.id === action.id);
          if (node) {
            const managerId = action.managerId === null ? 'virtual-root-org' : action.managerId;
            node.managerId = managerId;
            if (node.data) {
              node.data.managerId = managerId;
            }
          }
          const normalizedUser = this.users.find((u) => u.id === action.id);
          if (normalizedUser) {
            normalizedUser.managerId = action.managerId === null ? 'virtual-root-org' : action.managerId;
          }
          const originalUser = this.originalUsers.find((u) => u.id === action.id);
          if (originalUser) {
            originalUser.managerId = action.managerId;
          }
        });
        this.chart.data(this.users).render();
      }
      this.disableDrag();
    },

    undo() {
      const action = this.undoActions.pop();
      if (action) {
        const node = this.chart.getChartState().data.find((x) => x.id === action.id);
        const currentManagerId = node?.managerId || node?.data?.managerId;
        const currentManagerIdForRedo = currentManagerId === 'virtual-root-org' ? null : currentManagerId;
        
        this.redoActions.push({ id: action.id, managerId: currentManagerIdForRedo });
        
        const managerIdToSet = action.managerId === null ? 'virtual-root-org' : action.managerId;
        
        if (node) {
          node.managerId = managerIdToSet;
          if (node.data) {
            node.data.managerId = managerIdToSet;
          }
        }
        
        const normalizedUser = this.users.find((u) => u.id === action.id);
        if (normalizedUser) {
          normalizedUser.managerId = managerIdToSet;
        }
        
        const originalUser = this.originalUsers.find((u) => u.id === action.id);
        if (originalUser) {
          originalUser.managerId = action.managerId;
        }
        
        this.chart.data(this.users).render();
      }
    },

    redo() {
      const action = this.redoActions.pop();
      if (action) {
        const node = this.chart.getChartState().data.find((x) => x.id === action.id);
        const currentManagerId = node?.managerId || node?.data?.managerId;
        const currentManagerIdForUndo = currentManagerId === 'virtual-root-org' ? null : currentManagerId;

        this.undoActions.push({ id: action.id, managerId: currentManagerIdForUndo });
        
        const managerIdToSet = action.managerId === null ? 'virtual-root-org' : action.managerId;
        
        if (node) {
          node.managerId = managerIdToSet;
          if (node.data) {
            node.data.managerId = managerIdToSet;
          }
        }
        
        const normalizedUser = this.users.find((u) => u.id === action.id);
        if (normalizedUser) {
          normalizedUser.managerId = managerIdToSet;
        }
        
        const originalUser = this.originalUsers.find((u) => u.id === action.id);
        if (originalUser) {
          originalUser.managerId = action.managerId;
        }
        
        this.chart.data(this.users).render();
      }
    },

    goToUser(user) {
      if (!user || !this.chart) return;
      if (user.id === 'virtual-root-org') return;
      this.chart.clearHighlighting().setCentered(user.id).setUpToTheRootHighlighted(user.id).render();
    },

    getSubtree(rootId, allUsers) {
      const result = [];
      const queue = [rootId];
      const visited = new Set();
      while (queue.length > 0) {
        const currentId = queue.shift();
        if (visited.has(currentId)) continue;
        visited.add(currentId);
        const children = allUsers.filter(u => u.managerId === currentId);
        children.forEach(child => {
          result.push(child);
          queue.push(child.id);
        });
      }
      return result;
    },

    applyMyTreeFilter() {
      if (!this.currentUserId) return;
      const currentUser = this.originalUsers.find(u => u.id === this.currentUserId);
      if (!currentUser) return;

      const subtree = this.getSubtree(this.currentUserId, this.originalUsers);
      if (subtree.length === 0) {
        window.getApp.error('No tienes usuarios a tu cargo.');
        this.myTree = false;
        return;
      }

      // El usuario actual se convierte en la raíz sin virtual root
      this.users = [
        { ...currentUser, managerId: null },
        ...subtree,
      ];
      this.chart.data(this.users).render().fit();
    },

    onNodeClick(node) {
      if (!node || !this.chart || this.dragEnabled) return;
      if (node.id === 'virtual-root-org') return;
      this.$router.push({ name: 'UserEdit', params: { id: node.id }})
    },

    onChartContextMenu(event) {
      if (this.dragEnabled) return;
      const path = event.composedPath ? event.composedPath() : [];
      for (const el of path) {
        if (el === this.$refs.chartContainer) break;
        if (el.classList && el.classList.contains('node')) {
          const datum = d3.select(el).datum();
          if (!datum) break;
          const nodeId = datum.data?.id ?? datum.id;
          if (nodeId && nodeId !== 'virtual-root-org') {
            this.showNodeSidebar(nodeId);
          }
          break;
        }
      }
    },

    showNodeSidebar(nodeId) {
      const user = this.users.find(u => u.id === nodeId);
      if (!user) return;
      this.selectedNode = { ...user };
      this.sidebarOpen = true;
    },

    closeSidebar() {
      this.sidebarOpen = false;
      this.selectedNode = {};
      this.selectingUser = false;
      this.userSearch = '';
    },

    onSelectUser(userId) {
      this.selectingUser = false;
      this.userSearch = '';
      if (!userId) return;
      if (userId === 'virtual-root-org') {
        this.selectedNode = { ...this.selectedNode, managerId: null };
        return;
      }
      const user = this.originalUsers.find(u => u.id === userId);
      if (user) {
        this.$q.profilePicSrc(user);
        this.selectedNode = { ...user };
      }
    },

    goToUserEdit(userId) {
      this.$router.push({ name: 'UserEdit', params: { id: userId } });
    },

    onManagerChange(newManagerId) {
      const userId = this.selectedNode.id;
      if (!userId) return;

      // 'virtual-root-org' significa sin manager real → null para el servidor
      const normalizeId = id => (!id || id === 'virtual-root-org') ? null : id;

      const previousManagerId = normalizeId(this.selectedNode.managerId);
      const resolvedNewManagerId = normalizeId(newManagerId);

      // Sin cambio real, no hacer nada
      if (resolvedNewManagerId === previousManagerId) return;

      let url = `api/user/updateManager?id=${userId}`;
      if (resolvedNewManagerId) url += `&managerId=${resolvedNewManagerId}`;

      this.$http.post(url)
        .then(() => {
          // En el chart, null se representa como 'virtual-root-org'
          const chartManagerId = resolvedNewManagerId || 'virtual-root-org';

          this.selectedNode = { ...this.selectedNode, managerId: chartManagerId };

          const userInList = this.users.find(u => u.id === userId);
          if (userInList) userInList.managerId = chartManagerId;

          // originalUsers guarda el valor real (null, no 'virtual-root-org')
          const userInOriginal = this.originalUsers.find(u => u.id === userId);
          if (userInOriginal) userInOriginal.managerId = resolvedNewManagerId;

          this.chart.data(this.users).render();
          window.getApp.success('Manager actualizado correctamente');
        })
        .catch(() => {
          // Revertir: restaurar el valor normalizado del chart
          const revertId = previousManagerId || 'virtual-root-org';
          this.selectedNode = { ...this.selectedNode, managerId: revertId };
        });
    },

    generateContent(d) {
      const title = d.data.jobTitle || d.data.roles || '';
      return `
        <div class="node-container" style='width:${d.width}px;height:${d.height}px;padding:1px; background-color: var(--v-newDesignBackground-base)'>
          <div class="content-container" style="font-family: 'Inter', sans-serif; width:${d.width - 2}px;height:${d.height - 2}px;border-radius:10px;border: ${d.data._highlighted || d.data._upToTheRootHighlighted ? '3px solid #E27396' : '1px solid #167bd7'};overflow:hidden;display:flex;flex-direction:column;padding:10px 12px 10px 12px;box-sizing:border-box">
            <div class="v-avatar avatar cursor-pointer grey" style="height:36px;min-width:36px;width:36px;flex-shrink:0">
              <div class="v-image v-responsive theme--light">
                <div class="v-responsive__sizer" style="padding-bottom:85.75%;"></div>
                <div class="v-image__image v-image__image--cover" style="background-image:url('${d.data.profilePicSrc || ''}');background-position:center center;"></div>
                <div class="v-responsive__content" style="width:36px;"></div>
              </div>
            </div>
            <div style="margin-top:auto;${title ? '' : 'padding-bottom:0'}">
              <div style="font-size:13px;color:var(--v-text-base);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${d.data.blueTag}</div>
              ${title ? `<div style="color:#716E7B;margin-top:2px;font-size:10px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${title}</div>` : ''}
            </div>
          </div>
        </div>
      `;
    }
  }
};
</script>

<style lang="scss" scoped>
.user-autocomplete {
  max-width: 300px;
}
.col-login {
  padding-bottom: 0px !important;
  max-height: 120px !important;

  span {
    font-weight: 400;
    color:darkgrey;
    margin-bottom: 8px;
  }
}
.node-container { }
.content-container { background-color: #ffffff; }

.drag-enabled:not(.dragging-active) .node.draggable rect {
  stroke: grey;
  stroke-width: 3px;
  stroke-dasharray: 5px;
  /* stroke: var(--v-primary-base); */
  cursor: grab;
}

.drag-enabled.dragging-active .droppable rect {
  stroke: var(--v-primary-base);
  stroke-width: 3px;
  stroke-dasharray: 5px;
}

.node.dragging rect {
  stroke-dasharray: 0 !important;
  stroke-width: 0 !important;
}

.node.dragging .content-container {
  background-color: #ffffff;
  box-shadow: 0 5px 15px rgba(0,0,0,0.2);
  cursor: grabbing;
}

.node.drop-target .content-container {
  opacity: 0.5;
  background-color: #E0EDFE;
  transition: opacity 0.15s ease, background-color 0.15s ease;
}
</style>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500&display=swap');

.org-chart-wrapper {
  font-family: 'Inter', sans-serif;
  background-color: var(--v-newDesignBackground-base);
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.controls {
  padding: 10px;
  background: var(--v-newDesignBackground-base);;
  border-bottom: 1px solid var(--v-background-lighten2);
  display: flex;
  gap: 10px;
  align-items: center;
  z-index: 10;
}

.search-field {
  min-width: 200px;
  max-width: 240px;
}

.chart-and-sidebar {
  flex-grow: 1;
  display: flex;
  flex-direction: row;
  position: relative;
  overflow: hidden;
}

.chart-container {
  flex-grow: 1;
  min-width: 0;
  position: relative;
  overflow: hidden;
  background-color: var(--v-newDesignBackground-base);
}

.loading-container {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--v-newDesignBackground-base);
  z-index: 5;
}

.loading-message {
  font-size: 16px;
  color: #716E7B;
}
::v-deep path.link {
  fill: none;
  stroke: var(--v-primary-base);
  stroke-width: 2px;
  stroke-opacity: 0.6;
}
::v-deep path.link.link-highlighted {
  stroke: #E27396 !important;
  stroke-width: 3px !important;
  stroke-opacity: 1 !important;
}
/*
::v-deep .svg-chart-container {
  width: 100%;
}
*/

.action-btn {
  padding: 8px 16px;
  border-radius: 4px;
  border: 1px solid #ccc;
  cursor: pointer;
  background: var(--v-newDesignBackground-base);
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.action-btn.primary {
  background-color: var(--v-primary-base);
  color: white;
  border: none;
}
.action-btn.warning {
  background-color: var(--v-error-base) !important;
  color: white;
  border: none;
}

.drag-actions {
  display: flex;
  gap: 10px;
}

/* ── Node Sidebar ── */
.node-sidebar {
  position: absolute;
  top: 12px;
  right: 12px;
  bottom: 12px;
  width: 270px;
  background: var(--v-newDesignBackground-base);
  border: 1px solid var(--v-background-lighten2);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 16px 20px;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0,0,0,0.14);
  z-index: 20;
}

.sidebar-close {
  position: absolute;
  top: 10px;
  right: 10px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #716E7B;
  padding: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}
.sidebar-close:hover {
  background: var(--v-background-lighten2);
}

.sidebar-avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid #167bd7;
  flex-shrink: 0;
  position: relative;
  margin-top: 8px;
  margin-bottom: 12px;
  cursor: pointer;
}
.sidebar-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.sidebar-avatar-edit-overlay {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.15s;
}
.sidebar-avatar:hover .sidebar-avatar-edit-overlay {
  opacity: 1;
}

.sidebar-user-autocomplete {
  width: 100%;
}

.sidebar-name {
  font-size: 15px;
  font-weight: 600;
  color: #08011E;
  text-align: center;
  line-height: 1.3;
}
.sidebar-bluetag {
  font-size: 12px;
  color: #167bd7;
  margin-top: 3px;
  text-align: center;
}

.sidebar-section {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sidebar-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: #3a3a4a;
  line-height: 1.4;
}
.sidebar-icon {
  color: #716E7B;
  flex-shrink: 0;
  margin-top: 1px;
}
.sidebar-email {
  word-break: break-all;
}

.sidebar-label {
  font-size: 11px;
  font-weight: 600;
  color: #716E7B;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  width: 100%;
  margin-bottom: 2px;
}
.sidebar-chips {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
}

.sidebar-edit-btn {
  width: 100%;
  margin-top: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

/* slide-in animation */
@keyframes sidebar-slide-in {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.node-sidebar {
  animation: sidebar-slide-in 0.22s ease;
}
</style>